import React, {useEffect, useState} from 'react';
import {Box, Button, CircularProgress, Typography} from '@mui/material';
import {useAuth} from "../providers/AuthProvider";
import {useLocation} from "react-router-dom";
import {ScreenWrapper} from "./ScreenWrapper";

export const LoginScreen = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const {emailAuthLink, logInWithAuthLink} = useAuth();
    const location = useLocation();

    const validateEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleLogin = async () => {
        setError('');

        if (!validateEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        try {
            setIsSendingEmail(true);
            await emailAuthLink(email);
            setIsEmailSent(true);
        } catch (error) {
            setError('Error sending an email with a login link.');
        }
        setIsSendingEmail(false);
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const emailLogin = params.get('emailLogin');

        if (emailLogin !== 'true') {
            return;
        }

        logInWithAuthLink().then(() => {
            console.log('Customer authenticated successfully with email');
        }).catch(() => {
            setError('Error logging in. Please make sure you use the same device/browser for generating and opening the login link.');
        })
    }, [])

    return (
        <ScreenWrapper>
            <Box sx={{
                display: 'flex',
                padding: '10rem 3.12rem',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '1.25rem',
                flex: '1 0 0',
                alignSelf: 'stretch',
            }}>
                <Typography sx={{
                    display: 'flex',
                    height: '3.12rem',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignSelf: 'stretch',
                    color: '#FFF',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat, serif',
                    fontSize: '1.25rem',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    lineHeight: 'normal',
                }}>Login</Typography>
                <input
                    placeholder="Email (the login link will be emailed to you)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                        display: 'flex',
                        padding: '1rem 1.25rem',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        borderRadius: '8px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'rgba(255, 255, 255, 0.50)',
                        fontFamily: 'Inter, serif',
                        fontSize: '.87rem',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        border: 'none',
                        outline: 'none',
                    }}
                />
                <Button
                    variant="contained"
                    onClick={handleLogin}
                    sx={{
                        display: 'flex',
                        height: '3.12rem',
                        padding: '1rem 1.25rem',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '.62rem',
                        alignSelf: 'stretch',
                        borderRadius: '8px',
                        background: '#FF7044',
                        textTransform: 'none',
                        color: '#000',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Montserrat, serif',
                        fontSize: '.87rem',
                        fontStyle: 'normal',
                        fontWeight: '600',
                        lineHeight: 'normal',
                        "&.Mui-disabled": {
                            opacity: 0.4,
                            background: '#FF7044',
                            color: '#000',
                        },
                    }}
                    disabled={isSendingEmail || isEmailSent}
                >
                    {isSendingEmail ? <CircularProgress size="1.2rem" sx={{color: "#FFFFFF"}}/> : "Send Link"}
                </Button>
                {isEmailSent && <Typography variant="body2" sx={{
                    color: 'rgba(255, 255, 255, 0.50)',
                    textAlign: 'center',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Inter, serif',
                    fontSize: '.87rem',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: 'normal',
                    alignSelf: 'stretch',
                }}>A secure login-link has been emailed to you. Please check your inbox.</Typography>}
                {error && <Typography variant="body2" sx={{
                    color: '#FF7044',
                    textAlign: 'center',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Inter, serif',
                    fontSize: '.87rem',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: 'normal',
                    alignSelf: 'stretch',
                }}>{error}</Typography>}
            </Box>
        </ScreenWrapper>
    );
};
